// src/pages/MagazineDetail.js
import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchMagazinesByType } from "./magazineApi";
import Layout from "../components/layouts/Layout";
import magazineCover from "../images/JMA_logo.png";
import tw from "twin.macro";
import styled from "styled-components";
import apiClient from "../data/apiClient";

const BackLink = tw(
  Link,
)`text-lg text-primary-500 hover:text-primary-700 transition duration-300 ease-in-out mb-8 inline-flex items-center text-left`;
const BackIcon = tw.svg`w-5 h-5 mr-2`;
const Container = tw.div`relative bg-gray-100 text-gray-900 -mx-8 px-8 py-20 lg:py-24`;
const Heading = tw.h1`text-4xl font-bold text-center text-primary-500 mb-12 [font-family: 'CustomFont']`;
const MagazineGrid = tw.div`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8`;

const MagazineCard = styled.div`
  ${tw`bg-white rounded-lg shadow-lg overflow-hidden transform transition-all duration-300`}
  &:hover {
    ${tw`shadow-2xl -translate-y-2`}
  }
`;

const ImageContainer = tw.div`relative h-64 bg-gray-200`;
const MagazineImage = tw.img`w-full h-full object-cover`;
const FallbackIcon = tw.div`absolute inset-0 flex items-center justify-center bg-gray-300 text-gray-500`;

const CardContent = tw.div`p-6 text-center`;
const Title = tw.h3`text-xl font-semibold mb-4 h-16 flex items-center justify-center`;
const DownloadButton = styled.button`
  ${tw`inline-block bg-primary-500 text-gray-100 font-bold py-3 px-6 rounded-full transition-transform duration-300 cursor-pointer border-none`}
  &:hover {
    ${tw`bg-primary-700 scale-105`}
  }
  &:disabled {
    ${tw`opacity-50 cursor-not-allowed`}
  }
`;

const ButtonRow = tw.div`mt-4 flex items-center justify-center gap-3`;
const ShareButton = styled.button`
  ${tw`inline-block bg-white text-primary-600 border border-primary-500 font-bold py-3 px-6 rounded-full transition-colors duration-300`}
  &:hover {
    ${tw`bg-primary-100`}
  }
`;

const PaginationContainer = tw.div`flex justify-center items-center mt-16`;
const PageButton = styled.button`
  ${tw`mx-2 px-4 py-2 rounded-md font-semibold transition-colors duration-300`}
  ${({ disabled }) =>
    disabled && tw`bg-gray-300 text-gray-500 cursor-not-allowed`}
  ${({ active }) => active && tw`bg-primary-500 text-white`}
  ${({ active, disabled }) =>
    !active && !disabled && tw`bg-white text-primary-500 hover:bg-primary-100`}
`;

const MagazineDetail = () => {
  const { id } = useParams();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const issuesPerPage = 8;

  useEffect(() => {
    const getMagazines = async () => {
      try {
        setLoading(true);
        const data = await fetchMagazinesByType(id);
        setIssues(data);
      } catch (error) {
        console.error("Error fetching magazines:", error);
      } finally {
        setLoading(false);
      }
    };
    getMagazines();
  }, [id]);

  const indexOfLastIssue = currentPage * issuesPerPage;
  const indexOfFirstIssue = indexOfLastIssue - issuesPerPage;
  const paginatedIssues = issues.slice(indexOfFirstIssue, indexOfLastIssue);
  const totalPages = Math.ceil(issues.length / issuesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  if (loading) {
    return <div tw="flex justify-center items-center h-screen">Loading...</div>;
  }

  const handleDownload = async (issue) => {
    try {
      if (!issue.id) {
        alert("Magazine ID not available.");
        return;
      }

      // Call API endpoint to download magazine by ID
      const response = await apiClient.get(
        `/api/Magazine/download/${issue.id}`,
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      });

      // Create a temporary download link
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = issue.title || "magazine.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Download error:", error);
      alert("Failed to download file. Please try again or refresh the page.");
    }
  };

  const handleShare = async (issue) => {
    try {
      const shareData = {
        title: issue.title,
        text: issue.title,
        url: issue.file_path,
      };
      if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(issue.file_path);
        alert("Link copied to clipboard");
      } else {
        window.open(issue.file_path, "_blank");
      }
    } catch (e) {
      console.error("Share failed", e);
    }
  };

  return (
    <Layout>
      <Container>
        <BackLink to="/magazine">
          <BackIcon
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </BackIcon>
          Back
        </BackLink>
        <Heading>المجلات</Heading>
        {paginatedIssues.length > 0 ? (
          <MagazineGrid>
            {paginatedIssues.map((issue) => (
              <MagazineCard key={issue.id}>
                <ImageContainer>
                  {issue.image_path ? (
                    <MagazineImage src={issue.image_path} alt={issue.title} />
                  ) : (
                    <FallbackIcon>
                      <MagazineImage
                        src={magazineCover}
                        alt="Default Magazine Cover"
                      />
                    </FallbackIcon>
                  )}
                </ImageContainer>
                <CardContent>
                  <Title>{issue.title}</Title>
                  <ButtonRow>
                    <DownloadButton onClick={() => handleDownload(issue)}>
                      Download
                    </DownloadButton>
                    <ShareButton onClick={() => handleShare(issue)}>
                      Share
                    </ShareButton>
                  </ButtonRow>
                </CardContent>
              </MagazineCard>
            ))}
          </MagazineGrid>
        ) : (
          <div tw="text-center text-xl text-gray-600 mt-16">
            اس قسم میں کوئی رسالہ نہیں ملا۔
          </div>
        )}
        {totalPages > 1 && (
          <PaginationContainer>
            <PageButton
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              {"<"}
            </PageButton>
            {Array.from({ length: totalPages }, (_, index) => (
              <PageButton
                key={index + 1}
                onClick={() => handlePageChange(index + 1)}
                active={currentPage === index + 1}
              >
                {index + 1}
              </PageButton>
            ))}
            <PageButton
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              {">"}
            </PageButton>
          </PaginationContainer>
        )}
      </Container>
    </Layout>
  );
};

export default MagazineDetail;
